/**
 * DeveloperRegistrationForm Store
 * Zustand store for managing form state and access code validation
 */

import { create } from 'zustand';

/**
 * Access code validation result from the server
 */
export interface AccessCodeValidationResult {
  isValid: boolean;
  accessCodeType: string;
  salespersonEmail: string | null;
  errorMessage: string | null;
}

/**
 * Form submission response from the server
 */
export interface SubmitResponse {
  success: boolean;
  sessionId: string | null;
  stripeUrl: string | null;
  errorMessage: string | null;
  validationErrors: Record<string, string[]> | null;
}

/**
 * Initial state values
 */
const initialState = {
  // Access code validation state
  accessCodeValidating: false,
  accessCodeResult: null as AccessCodeValidationResult | null,
  accessCodeError: null as string | null,

  // Form submission state
  isSubmitting: false,
  submitError: null as string | null,
  fieldErrors: {} as Record<string, string>,
};

/**
 * Store state interface
 */
interface DeveloperRegistrationFormState {
  // Access code validation state
  accessCodeValidating: boolean;
  accessCodeResult: AccessCodeValidationResult | null;
  accessCodeError: string | null;

  // Form submission state
  isSubmitting: boolean;
  submitError: string | null;
  fieldErrors: Record<string, string>;

  // Actions
  setAccessCodeValidating: (validating: boolean) => void;
  setAccessCodeResult: (result: AccessCodeValidationResult | null) => void;
  setAccessCodeError: (error: string | null) => void;
  setSubmitting: (submitting: boolean) => void;
  setSubmitError: (error: string | null) => void;
  setFieldErrors: (errors: Record<string, string>) => void;
  clearFieldError: (field: string) => void;
  reset: () => void;

  // For testing
  _reset: () => void;
}

/**
 * Zustand store for DeveloperRegistrationForm
 */
export const useDeveloperRegistrationFormStore = create<DeveloperRegistrationFormState>((set) => ({
  // Initial state
  ...initialState,

  // Actions
  setAccessCodeValidating: (accessCodeValidating) => set({ accessCodeValidating }),

  setAccessCodeResult: (accessCodeResult) =>
    set({
      accessCodeResult,
      accessCodeValidating: false,
      accessCodeError: null,
    }),

  setAccessCodeError: (accessCodeError) =>
    set({
      accessCodeError,
      accessCodeValidating: false,
      accessCodeResult: null,
    }),

  setSubmitting: (isSubmitting) => set({ isSubmitting }),

  setSubmitError: (submitError) => set({ submitError }),

  setFieldErrors: (fieldErrors) => set({ fieldErrors }),

  clearFieldError: (field) =>
    set((state) => {
      const { [field]: _removed, ...rest } = state.fieldErrors;
      return { fieldErrors: rest };
    }),

  reset: () => set({ ...initialState }),

  // Reset to initial state (for testing)
  _reset: () => set(initialState),
}));
